import { useEffect, useRef, type ReactNode } from 'react'
import { AnimatePresence, animate, motion, useMotionValue, useReducedMotion } from 'motion/react'
import { Croix } from './Icone.tsx'

/**
 * Feuille basse — le geste principal de l'application.
 *
 * Réécrite sur des RESSORTS après revue. La version précédente était à base
 * de transitions CSS à durée fixe, et elle accumulait cinq défauts que l'on
 * sent sur un téléphone sans forcément savoir les nommer :
 *
 * 1. LA VITESSE ÉTAIT UNE MOYENNE, pas une vitesse instantanée. On divisait
 *    le déplacement TOTAL par le temps TOTAL : un glissement lent suivi d'un
 *    coup sec donnait une moyenne basse, et le geste ne fermait pas. On
 *    échantillonne désormais les derniers ~100 ms.
 *
 * 2. AUCUNE REPRISE DE VITESSE au relâchement. Une transition de 300 ms à
 *    durée fixe ignore la vitesse du doigt : il y avait une couture visible
 *    entre le glissement et l'animation. Le ressort repart maintenant À la
 *    vitesse du doigt.
 *
 * 3. L'ANIMATION D'ENTRÉE REJOUAIT après un glissement annulé. Elle était
 *    conditionnée à `decalage === 0`, et revenir à 0 après avoir relâché
 *    sous le seuil la redéclenchait : la feuille rentrait une seconde fois.
 *
 * 4. NON INTERRUPTIBLE DEPUIS LA VALEUR AFFICHÉE. Reprendre la feuille en
 *    plein vol repartait de la dernière valeur d'état React, pas de sa
 *    position réelle à l'écran — d'où un saut. Un ressort anime toujours
 *    depuis la valeur courante.
 *
 * 5. PROJECTION TROP FAIBLE. `d + v * 150` sous-estimait très largement la
 *    course d'un coup sec. On utilise la formule d'Apple, à décroissance
 *    exponentielle.
 */

/** Au-delà, on ferme. Sous ce seuil, c'est la VITESSE qui tranche. */
const SEUIL_DISTANCE = 110

/**
 * Projection de la position de repos, formule d'Apple (Designing Fluid
 * Interfaces). Ce n'est PAS le v²/2a des manuels : c'est une décroissance
 * exponentielle, qui correspond au comportement réel du défilement iOS.
 *
 * 0.99 et NON 0.998. La constante d'Apple pour le défilement (0.998)
 * projette environ un demi-pixel par pixel/seconde : à 400 px/s elle
 * annonce 200 px de course. C'est juste pour une liste qu'on lance, et
 * beaucoup trop pour une feuille — n'importe quel glissement vers le bas la
 * fermait. 0.99 projette dix fois moins, ce qui correspond à une surface
 * qu'on repousse plutôt qu'à un contenu qu'on jette.
 */
function projeter(vitessePxParSeconde: number, deceleration = 0.99): number {
  return ((vitessePxParSeconde / 1000) * deceleration) / (1 - deceleration)
}

/**
 * Au-delà de ce silence, on considère le doigt IMMOBILE et la vitesse nulle.
 *
 * Sans ce garde-fou, s'arrêter puis relâcher conservait la vitesse d'avant
 * l'arrêt : la feuille partait alors qu'on venait justement de se raviser.
 * C'est exactement ce que fait iOS — on retient un défilement en posant le
 * doigt, et rien ne file au relâchement.
 */
const SILENCE_MS = 80

export function Sheet({
  ouverte,
  onFermer,
  titre,
  children,
}: {
  ouverte: boolean
  onFermer: () => void
  titre: string
  children: ReactNode
}) {
  const y = useMotionValue(0)
  const panneau = useRef<HTMLDivElement>(null)
  const reduit = useReducedMotion()

  /*
   * Historique court des derniers points. C'est ce qui donne une vitesse
   * INSTANTANÉE : seuls les ~100 dernières millisecondes comptent, sinon un
   * long glissement lent noie le coup sec final.
   */
  const trace = useRef<Array<{ y: number; t: number }>>([])
  const prise = useRef<number | null>(null)
  /*
   * Le ressort en cours, pour pouvoir l'ARRÊTER quand on rattrape la
   * feuille en plein vol.
   *
   * Sans ça, le ressort continue de pousser `y` vers sa cible pendant que
   * le doigt écrit la même valeur : les deux se disputent la propriété, et
   * la feuille saute au moment précis où l'on veut la reprendre en main.
   * L'interruptibilité ne consiste pas seulement à animer depuis la valeur
   * affichée — il faut aussi rendre la main.
   */
  const ressort = useRef<{ stop: () => void } | null>(null)

  useEffect(() => {
    if (ouverte) y.set(0)
  }, [ouverte, y])

  useEffect(() => {
    if (!ouverte) return
    const f = (e: KeyboardEvent) => e.key === 'Escape' && onFermer()
    window.addEventListener('keydown', f)
    return () => window.removeEventListener('keydown', f)
  }, [ouverte, onFermer])

  function vitesse(): number {
    const h = trace.current
    if (h.length < 2) return 0
    const fin = h[h.length - 1]!

    // Doigt immobile juste avant le relâchement : on se ravise, rien ne part.
    if (performance.now() - fin.t > SILENCE_MS) return 0

    // Fenêtre courte : seules les ~100 dernières millisecondes comptent,
    // sinon un long glissement lent noie le coup sec final.
    const debut = h.find((p) => fin.t - p.t <= 100) ?? h[0]!
    const dt = fin.t - debut.t
    return dt > 0 ? ((fin.y - debut.y) / dt) * 1000 : 0
  }

  return (
    <AnimatePresence>
      {ouverte && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          {/*
            Le voile FOND, il n'apparaît pas d'un coup. L'ancienne version
            portait une transition d'opacité sans jamais changer l'opacité :
            elle ne servait à rien et le voile claquait.
          */}
          <motion.button
            type="button"
            aria-label="Fermer"
            onClick={onFermer}
            className="absolute inset-0 cursor-default bg-scrim"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
          />

          <motion.div
            ref={panneau}
            role="dialog"
            aria-modal="true"
            aria-label={titre}
            className="sheet relative max-h-[85dvh] overflow-hidden rounded-t-[var(--radius-sheet)] bg-surface shadow-sheet"
            style={{ y }}
            initial={reduit ? { opacity: 0 } : { y: '100%' }}
            animate={reduit ? { opacity: 1 } : { y: 0 }}
            exit={reduit ? { opacity: 0 } : { y: '100%' }}
            /*
             * Ressort critique (bounce 0) : une feuille qui s'ouvre n'a pas
             * de momentum derrière elle, un rebond y serait gratuit. Le
             * rebond est réservé au relâchement d'un geste — voir plus bas.
             */
            transition={{ type: 'spring', bounce: 0, duration: 0.42 }}
          >
            {/* touch-pan-x : la poignée possède le vertical, le navigateur
                garde l'horizontal. */}
            <div
              className="flex h-9 cursor-grab touch-pan-x select-none items-center justify-center"
              onPointerDown={(e) => {
                ressort.current?.stop()
                ressort.current = null
                e.currentTarget.setPointerCapture(e.pointerId)
                // On mémorise l'OFFSET de prise : la feuille ne doit pas
                // sauter sous le doigt au moment où on l'attrape.
                prise.current = e.clientY - y.get()
                trace.current = [{ y: e.clientY, t: performance.now() }]
              }}
              onPointerMove={(e) => {
                if (prise.current === null) return
                const brut = e.clientY - prise.current
                /*
                 * Rubber-band vers le haut : on résiste progressivement au
                 * lieu de bloquer net. Rien ne s'arrête d'un coup dans le
                 * monde réel — ça ralentit d'abord.
                 */
                y.set(brut < 0 ? brut * 0.55 : brut)
                trace.current.push({ y: e.clientY, t: performance.now() })
                if (trace.current.length > 12) trace.current.shift()
              }}
              onPointerUp={() => {
                if (prise.current === null) return
                prise.current = null
                const v = vitesse()
                const position = y.get()

                /*
                 * On décide sur la position PROJETÉE, pas sur la position
                 * atteinte : un coup sec et court doit fermer, un long
                 * glissement lent jusqu'à mi-hauteur ne doit pas.
                 */
                if (position + projeter(v) > SEUIL_DISTANCE) {
                  onFermer()
                } else {
                  /*
                   * Retour en place AVEC la vitesse du doigt. C'est ce qui
                   * supprime la couture entre le geste et l'animation : la
                   * feuille ne repart pas de zéro, elle continue.
                   *
                   * Un léger rebond ici, et seulement ici : le geste portait
                   * du momentum, donc le dépassement est physiquement juste.
                   */
                  ressort.current = animate(y, 0, {
                    type: 'spring',
                    bounce: reduit ? 0 : 0.18,
                    duration: 0.42,
                    // La vitesse du doigt devient celle du ressort : c'est
                    // ce qui supprime la couture entre le geste et
                    // l'animation.
                    velocity: v,
                  })
                }
                trace.current = []
              }}
              onPointerCancel={() => {
                prise.current = null
                y.set(0)
              }}
            >
              <span aria-hidden className="h-1 w-9 rounded-full bg-line-strong" />
            </div>

            {/*
              Bouton de fermeture EXPLICITE, en plus de la poignée.
              
              Le glissement vers le bas est le geste natural sur iPhone, mais
              il n'est pas découvrable : sur une feuille dont le contenu
              remplit tout l'écran, il ne reste presque plus de voile à
              toucher au-dessus, et rien ne dit comment sortir. La question
              « comment je valide ? » est arrivée exactement là.
            */}
            <div className="flex items-start justify-between gap-3 px-4 pb-1">
              <h2 className="min-w-0 flex-1 font-serif text-title text-ink">{titre}</h2>
              <button
                type="button"
                onClick={onFermer}
                aria-label="Fermer"
                className="pressable -mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-surface-sunk text-muted"
              >
                <Croix className="size-4" />
              </button>
            </div>

            {/* overscroll-contain : le défilement interne ne doit jamais
                déborder sur la page derrière, sinon la feuille « tire »
                l'écran. */}
            <div
              className="overflow-y-auto overscroll-contain px-4"
              style={{
                maxHeight: 'calc(85dvh - 96px)',
                // 24 px et non 16 : le dernier élément d'une feuille est
                // presque toujours son action principale, et collée à
                // l'indicateur d'accueil elle est difficile à viser au pouce.
                paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
              }}
            >
              {children}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
